// src/frontend/data/books/BlockchainFundamentals/data/cap3.js

const CAP3 = {
  id: 3,
  slug: "cap3-usdt",
  title:
    "Capítulo 3 — USDT (Tether): stablecoins, paridad 1:1, emisión, Proof of Reserves, riesgos y uso práctico",
  sections: [
    {
      heading: "¿Qué es USDT (Tether) y por qué existen las stablecoins?",
      meta: {
        date: "2019-09-24",
        updated: "2023-02-07",
        level: "Básico",
        readTime: "12 min",
      },
      text: [
        "USDT (Tether) es una stablecoin: una criptomoneda diseñada para mantener un valor estable, normalmente ligado a una moneda fiat (por ejemplo, el dólar).",
        "La idea central de una stablecoin es reducir la volatilidad típica de criptomonedas como BTC o ETH. Por eso se usan como “puente” entre el mundo cripto y la estabilidad del dinero fiat.",
        "En el caso de USDT, su objetivo es sostener paridad 1:1 con el dólar estadounidense. Es decir: 1 USDT debería valer ~1 USD (muy cercano), incluso cuando el mercado cripto es volátil.",
        "El nombre USDT se entiende como USD + T (Tether). Y aunque para el usuario parece “solo un dólar en blockchain”, las implicaciones de operar esa paridad son enormes: requieren emisión controlada, mecanismos de canje, reservas, transparencia y gestión de riesgos.",
        "Por eso USDT no es solo un token: es un ecosistema operativo donde una empresa (Tether) emite, gestiona canjes, mantiene infraestructura y publica reportes de reservas/activos.",
      ],
    },

    {
      heading: "Historia de USDT: de Mastercoin/Omni a su expansión multi-cadena",
      text: [
        "La idea que dio origen a USDT se asocia al trabajo alrededor de Mastercoin (2014), un protocolo construido sobre Bitcoin que permitía crear capas para representar activos y tokens.",
        "En ese contexto aparecen nombres clave como J.R. Willet, y el desarrollo que evolucionó hacia Omni Layer (la evolución del enfoque tipo Mastercoin).",
        "Sobre Omni Layer se lanzó Realcoin (2014) y posteriormente cambió de nombre a “Tether”. En ese momento se presentaron varias monedas: USDt (USDT), EURt (EURT) y YENt (YENT).",
        "La adopción práctica despega cuando plataformas de trading comienzan a listar USDT, porque resuelve un problema real: moverse a “dólar” sin salir de la infraestructura cripto.",
        "Luego USDT se expande: además de Omni (Bitcoin), aparecen implementaciones en otras redes (por ejemplo, ERC-20 en Ethereum) y, con los años, se vuelve un activo multi-cadena para circular donde haya demanda y liquidez.",
      ],
    },

    {
      heading: "¿Para qué sirve USDT? (utilidad real en mercados y pagos)",
      text: [
        "USDT tiene sentido porque combina dos mundos: (1) la movilidad global y 24/7 de la blockchain y (2) una referencia de valor estable (dólar) para protegerse de la volatilidad.",
        "En mercados cripto, USDT funciona como “moneda puente” (base pairs). Es común que traders roten capital de activos volátiles hacia USDT para esperar oportunidades o reducir riesgo temporal.",
        "Para exchanges, USDT aporta liquidez y rapidez operativa: permite mover “dólares” entre plataformas sin depender de bancos, horarios o transferencias internacionales lentas.",
        "En pagos, USDT sirve para casos donde no quieres volatilidad: remesas, pagos internacionales, cobros, protección temporal del valor, y operaciones comerciales donde el precio en dólares es importante.",
      ],
      list: [
        "Transferencias globales con costos potencialmente menores que banca tradicional (según red y congestión).",
        "Protección de traders ante volatilidad al “estacionarse” en dólar tokenizado.",
        "Liquidez y pares de trading (muchos mercados se mueven contra stablecoins).",
        "Pagos y cobros con referencia estable (cuando la volatilidad no es deseable).",
      ],
    },

    {
      heading:
        "Cómo funciona técnicamente USDT: emisión, destrucción (burn) y redes donde circula",
      text: [
        "USDT no se mina: se emite (“mint”) y se destruye (“burn”) según necesidades del ecosistema y el flujo de depósitos/canjes que maneja Tether.",
        "A nivel técnico, USDT puede existir en múltiples redes. Eso significa que el mismo “USDT” puede circular como token en varias blockchains (por ejemplo, Ethereum, TRON, etc.), con distintos costos y velocidades según la red.",
        "La consecuencia práctica es importante: el usuario debe elegir red correctamente al depositar/retirar (por ejemplo, USDT-ERC20 vs USDT-TRC20), porque enviar a la red equivocada puede causar pérdida de fondos.",
        "En el texto que compartiste, se menciona presencia en múltiples redes (por ejemplo: Ethereum, EOS, TRON, Algorand, Avalanche, Omni, Polygon, Tezos, Solana, Polkadot, Bitcoin Cash, entre otras). Este dato es útil como visión de alcance, pero en la práctica tu app/curso debe enseñar a verificar siempre la red exacta en cada plataforma.",
      ],
      note: [
        "Tip didáctico: en UI/UX del curso, muestra siempre ‘Red’ + ‘Comisión estimada’ + ‘Tiempo esperado’ para depósitos/retiros de USDT.",
      ],
    },

    {
      heading:
        "Proof of Reserves y “Equivalencia Tether”: la promesa de paridad 1:1",
      text: [
        "La gran pregunta con cualquier stablecoin es: ¿qué sostiene el valor 1:1? En USDT, la narrativa es que existe una relación de colateralización que permite respaldar el suministro emitido.",
        "En tu texto aparece el concepto de “Prueba de Reservas (Proof of Reserves)” y la idea de conectar contabilidad on-chain con contabilidad bancaria/activos para sostener lo que llamas “Equivalencia Tether”.",
        "La idea educativa que debes transmitir aquí es simple: si USDT pretende valer 1 dólar, debe existir un mecanismo de respaldo + canje que mantenga la paridad en la práctica (a través de mercado y redenciones).",
        "Aquí es donde entran dos temas críticos para el alumno: (1) transparencia (reportes, reservas, composición de activos) y (2) riesgo (qué pasa si reservas no alcanzan, o si hay eventos regulatorios/operativos).",
      ],
      images: [
        {
          // ✅ IMAGEN 1 (la que me mandaste aquí)
          src: "/images/books/BlockchainFundamentals/usdt/tether-proof-reserves-flow.png",
          alt: "Diagrama de capas / flujo y Proof of Reserves en USDT (Tether)",
          caption:
            "Flujo conceptual: ecosistema Tether + emisión/circulación + capa de consenso (visual didáctico para explicar 1:1 y Proof of Reserves).",
        },
      ],
    },

    {
      heading:
        "Flujo de dinero y tokens en USDT: el ciclo depósito → emisión → uso → canje → destrucción",
      text: [
        "Para entender USDT, piensa en un ciclo operativo. El objetivo del ciclo es mantener la paridad: cuando entra dinero (o se solicita emisión), se crean tokens; cuando se redime por fiat, esos tokens se destruyen.",
        "En tu texto se describe un flujo por etapas. Lo dejo aquí de forma clara y didáctica para tu curso:",
      ],
      list: [
        "1) El usuario deposita dinero (por ejemplo USD) siguiendo el proceso habilitado por Tether o por una plataforma que canaliza el acceso.",
        "2) Se emiten (mint) tokens USDT en proporción 1:1 a ese depósito (modelo conceptual).",
        "3) El usuario usa USDT como cualquier cripto: envía, recibe, tradea, paga, mueve entre redes (según soporte).",
        "4) Para canjear, el usuario entrega USDT (redeem) y solicita recibir dólares (u otro equivalente) vía los mecanismos del emisor o intermediarios.",
        "5) Los tokens canjeados se destruyen (burn) y el emisor liquida el pago en fiat según el proceso establecido.",
      ],
      note: [
        "En la vida real, muchas personas obtienen USDT en exchanges y no interactúan directamente con Tether. Aun así, entender el ciclo te ayuda a evaluar riesgos y funcionamiento del “peg”.",
      ],
    },

    {
      heading:
        "Riesgos, controversias y límites: lo que un alumno debe saber antes de usar USDT",
      text: [
        "USDT es muy usado, pero también ha sido objeto de debate público. En tu texto se mencionan puntos sensibles como auditorías incompletas, renuncias de auditores, opacidad, demandas, y preocupación por composición real de reservas.",
        "Este apartado es clave: no se trata de meter miedo, sino de enseñar criterio. Una stablecoin puede ser útil y al mismo tiempo tener riesgos específicos distintos a BTC/ETH.",
        "En el curso conviene explicar que la estabilidad 1:1 no es magia: depende de confianza de mercado, acceso a canje/redención, liquidez, y respaldo percibido (y/o real) en reservas.",
        "También es importante enseñar que stablecoins centralizadas suelen tener controles: congelamiento de direcciones, cumplimiento regulatorio y dependencias operativas.",
      ],
      list: [
        "Riesgo de transparencia: qué tan verificables son reservas y pasivos.",
        "Riesgo de contraparte: depende de una empresa (no es descentralizada).",
        "Riesgo regulatorio: cambios legales pueden afectar emisión, canje o exchanges.",
        "Riesgo de mercado: en crisis de confianza, el ‘peg’ puede desviarse temporalmente.",
        "Riesgo operativo: usar la red incorrecta (ERC-20 vs TRC-20, etc.) puede causar pérdida de fondos.",
      ],
    },

    {
      heading:
        "Cómo usar USDT de forma segura: wallets, redes, comisiones y buenas prácticas",
      text: [
        "USDT se usa comúnmente desde exchanges y wallets. Para operar con seguridad, el alumno debe dominar tres hábitos: (1) verificar red, (2) verificar dirección, (3) hacer pruebas con montos pequeños.",
        "En wallets, USDT no es “un solo USDT”: es USDT en una red específica. Eso determina comisiones, velocidad y compatibilidad.",
        "Además, si el alumno compra USDT en un exchange, normalmente habrá KYC. En el texto se menciona que adquirir USDT desde el emisor también exige procesos de verificación (según política).",
        "En un curso práctico, lo ideal es incluir ejemplos visuales: pantalla de retiro con selector de red + aviso de compatibilidad + advertencia de pérdida si se elige mal.",
      ],
      list: [
        "Verifica la red exacta al enviar/recibir (ERC-20, TRC-20, etc.).",
        "Haz una prueba primero (monto pequeño) antes de mover cantidades grandes.",
        "Copia/pega direcciones y revisa primeros/últimos caracteres.",
        "Desconfía de links y ‘soporte’ por DM; nadie legítimo te pide semillas.",
        "Guarda respaldos de wallet y usa 2FA en exchanges.",
      ],
    },

    {
      heading: "Ventajas y desventajas (resumen limpio para el alumno)",
      text: [
        "USDT es útil por su estabilidad y liquidez, pero tiene riesgos típicos de stablecoins centralizadas. En tu curso, este bloque debe quedar como una checklist rápida.",
      ],
      list: [
        "✅ Ventajas: precio estable (~1 USD), alta integración en exchanges, liquidez, útil para trading y pagos sin volatilidad.",
        "✅ Ventajas: facilita moverse entre cripto y “dólar” sin salir de blockchain.",
        "⚠️ Desventajas: depende de una empresa (centralización), dudas/percepción sobre auditorías o reservas, y exposición a regulación.",
        "⚠️ Desventajas: no es anónimo si se usa con KYC (emisor o exchange).",
      ],
    },

    {
      heading: "Conclusión",
      text: [
        "USDT es una pieza central del ecosistema cripto porque resuelve un problema real: estabilidad operativa en un mercado volátil.",
        "Su valor está en la utilidad (liquidez, pagos, trading) y su riesgo está en la dependencia (emisor, reservas, regulación).",
        "Si el alumno entiende paridad 1:1, Proof of Reserves, redes, y el ciclo emisión/canje, tendrá criterio para usar USDT con seguridad y sin confusiones.",
      ],
    },
  ],
};

export default CAP3;
